/**
 * KeywordSweep — sequential keyword highlighter inspired by
 * quantumemotion.com's 2nd-section paragraph.
 *
 * Splits a sentence into segments where the listed keywords are wrapped
 * in animated `<span>`s. Each keyword lights up in `accent` for ~1s, then
 * fades back to the body text colour, with the next keyword picking up
 * where the previous left off. Loops indefinitely.
 *
 * Implementation notes:
 *  - One CSS keyframe animation per keyword, with the same total
 *    duration but a unique `animation-delay` so they fire in sequence.
 *  - Animation's color stays at the muted text colour for most of the
 *    cycle, briefly transitions through the accent at this keyword's
 *    "slot", then returns. We compute the `% stops` from the index +
 *    total keyword count so the loop is seamless and even.
 *  - The wrapping is done case-INSENSITIVE so writers don't have to
 *    match casing, but the highlighted word keeps its original casing
 *    from the prose.
 *  - `respects prefers-reduced-motion` automatically.
 */
import React, { useMemo } from "react";

export default function KeywordSweep({
  text,
  keywords = [],
  accent = "#00E5FF",       // QorTrace cyan by default
  base   = "var(--qt-text-2)",
  perKeywordMs = 1200,      // each keyword's lit window
  className = "",
  style = {},
  testId,
}) {
  // Build segments + a unique class per keyword so we can CSS-animate them
  const { segments, css } = useMemo(
    () => buildSegments(text, keywords, accent, base, perKeywordMs),
    [text, keywords, accent, base, perKeywordMs],
  );

  return (
    <p className={className} style={style} data-testid={testId}>
      <style>{css}</style>
      {segments.map((seg, i) =>
        seg.kw ? (
          <span key={i}
                className={`qt-kw qt-kw-${seg.idx}`}
                data-kw={seg.idx}>
            {seg.text}
          </span>
        ) : (
          <React.Fragment key={i}>{seg.text}</React.Fragment>
        ),
      )}
    </p>
  );
}

function buildSegments(text, keywords, accent, base, perKeywordMs) {
  if (!keywords?.length) {
    return { segments: [{ text }], css: "" };
  }
  // Split text on the keywords, keeping them.
  // Sort by length desc so multi-word phrases are matched before sub-words.
  const sorted = [...keywords].sort((a, b) => b.length - a.length);
  const escaped = sorted.map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`(${escaped.join("|")})`, "gi");

  const parts = text.split(re);
  // Map keyword text → stable idx so duplicate keywords share an animation
  const idxFor = new Map();
  sorted.forEach((k, i) => idxFor.set(k.toLowerCase(), i));

  const segments = parts.map((p) => {
    const idx = idxFor.get(p.toLowerCase());
    if (idx !== undefined) {
      return { text: p, kw: true, idx };
    }
    return { text: p, kw: false };
  });

  // Total cycle length: every keyword gets its own slot, plus a tail
  // pause so the loop breathes.
  const n = sorted.length;
  const cycleMs = perKeywordMs * (n + 1);

  // For each keyword index `i`, build a keyframe that lights up at
  // its slot and is muted everywhere else.
  let css = `
    @media (prefers-reduced-motion: no-preference) {
      .qt-kw {
        color: ${base};
        transition: color 0.18s ease-out;
        will-change: color;
      }
  `;
  for (let i = 0; i < n; i++) {
    const start = (i / (n + 1)) * 100;
    const peak  = ((i + 0.45) / (n + 1)) * 100;
    const end   = ((i + 0.9) / (n + 1)) * 100;
    css += `
      @keyframes qt-kw-${i} {
        0%, ${start.toFixed(2)}%   { color: ${base}; }
        ${peak.toFixed(2)}%        { color: ${accent};
                                     text-shadow: 0 0 14px ${accent}66; }
        ${end.toFixed(2)}%         { color: ${base};
                                     text-shadow: none; }
        100%                       { color: ${base}; }
      }
      .qt-kw-${i} {
        animation: qt-kw-${i} ${cycleMs}ms cubic-bezier(.4,.0,.2,1) infinite;
      }
    `;
  }
  css += `}
    @media (prefers-reduced-motion: reduce) {
      .qt-kw { color: ${accent}; font-weight: 500; }
    }
  `;
  return { segments, css };
}
